import React from 'react';
import { Box, Button, Tooltip, CircularProgress, Typography, Paper, Divider } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import PlaylistAddCheck from '@mui/icons-material/PlaylistAddCheck';
import BlockIcon from '@mui/icons-material/Block';

export interface ProcessAction {
  label: string;
  icon?: React.ReactNode;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  description: string;
  href?: string;
  external?: boolean;
  ruleBlocked?: boolean;
  ruleReason?: string;
}

interface ProcessActionsProps {
  actions: ProcessAction[];
  isLoading?: boolean;
  isEvaluating?: boolean;
  title?: string;
}

export const ProcessActions: React.FC<ProcessActionsProps> = ({
  actions,
  isLoading = false,
  isEvaluating = false,
  title = 'Process Actions',
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        my: 3,
        bgcolor: 'action.hover',
        borderTop: 4,
        borderTopColor: 'primary.main',
        borderLeft: 0,
        borderRight: 0,
        borderBottom: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PlaylistAddCheck fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {(isLoading || isEvaluating) && (
          <CircularProgress size={14} sx={{ color: 'text.disabled', ml: 'auto' }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {actions.map((action, index) => {
          const blocked = action.ruleBlocked || false;
          const reason = action.ruleReason;
          const tooltipContent = blocked && reason ? (
            <Box sx={{ p: 0.5, maxWidth: 280 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <BlockIcon fontSize="inherit" color="warning" />
                Guard Blocked
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {reason}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                {action.label}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {action.description}
              </Typography>
            </Box>
          );

          return (
            <Tooltip
              key={index}
              title={tooltipContent}
              arrow
              placement="top"
            >
              <span>
                <Button
                  variant={action.variant || 'contained'}
                  color={action.color || 'primary'}
                  size="small"
                  startIcon={
                    action.loading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : blocked ? (
                      <BlockIcon fontSize="inherit" />
                    ) : (
                      action.icon
                    )
                  }
                  onClick={action.onClick}
                  disabled={action.disabled || action.loading || isLoading || isEvaluating || blocked}
                >
                  {action.label}
                </Button>
              </span>
            </Tooltip>
          );
        })}
      </Box>
    </Paper>
  );
};

interface ActionGroup {
  label: string;
  description: string;
  icon?: React.ReactNode;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  condition?: boolean;
  ruleBlocked?: boolean;
  ruleReason?: string;
}

interface ProcessActionsGroupedProps {
  groups: Array<{
    title?: string;
    actions: ActionGroup[];
  }>;
  isLoading?: boolean;
  isEvaluating?: boolean;
}

export const ProcessActionsGrouped: React.FC<ProcessActionsGroupedProps> = ({
  groups,
  isLoading = false,
  isEvaluating = false,
}) => {
  const nonEmptyGroups = groups.filter(g => g.actions.some(a => a.condition !== false));
  if (nonEmptyGroups.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        my: 3,
        bgcolor: 'action.hover',
        borderTop: 4,
        borderTopColor: 'primary.main',
        borderLeft: 0,
        borderRight: 0,
        borderBottom: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PlaylistAddCheck fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Process Actions
        </Typography>
        {(isLoading || isEvaluating) && (
          <CircularProgress size={14} sx={{ color: 'text.disabled', ml: 'auto' }} />
        )}
      </Box>
      {nonEmptyGroups.map((group, groupIndex) => {
        const visibleActions = group.actions.filter(a => a.condition !== false);
        if (visibleActions.length === 0) return null;

        return (
          <Box key={groupIndex}>
            {groupIndex > 0 && <Divider sx={{ my: 1.5 }} />}
            {group.title && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  color: 'text.secondary',
                  display: 'block',
                  mb: 1,
                }}
              >
                {group.title}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {visibleActions.map((action, index) => {
                const blocked = action.ruleBlocked || false;
                const reason = action.ruleReason;
                const tooltipContent = blocked && reason ? (
                  <Box sx={{ p: 0.5, maxWidth: 280 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <BlockIcon fontSize="inherit" color="warning" />
                      Guard Blocked
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {reason}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 0.5, maxWidth: 280 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                      {action.label}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {action.description}
                    </Typography>
                  </Box>
                );

                return (
                  <Tooltip
                    key={index}
                    title={tooltipContent}
                    arrow
                    placement="top"
                  >
                    <span>
                      <Button
                        variant={action.variant || 'contained'}
                        color={action.color || 'primary'}
                        size="small"
                        startIcon={
                          action.loading ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : blocked ? (
                            <BlockIcon fontSize="inherit" />
                          ) : (
                            action.icon
                          )
                        }
                        onClick={action.onClick}
                        disabled={action.disabled || action.loading || isLoading || isEvaluating || blocked}
                      >
                        {action.label}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
};
